/*
  Motore di animazione globale del tema — GSAP core (nessun plugin richiesto).

  Cosa fa:
  - Rivela gli elementi quando entrano nel viewport (IntersectionObserver + GSAP).
  - Fa da ponte con le classi di Dawn (.scroll-trigger): le sezioni esistenti si animano
    senza dover modificare i loro file .liquid.
  - Titoli animati parola per parola, parallasse leggera sui banner, hover delle card prodotto.
  - Rispetta prefers-reduced-motion tramite gsap.matchMedia(): con motion ridotto non anima nulla.

  Attributi disponibili nel markup:
  - data-motion="rise|fade|zoom|words|unclip"  variante d'ingresso (default: rise)
  - data-motion-stagger                 anima i figli in sequenza invece dell'elemento stesso
  - data-motion-delay="0.2"             ritardo in secondi
  - data-motion-parallax="0.15"         parallasse sull'immagine contenuta (intensità opzionale)
*/

(function () {
  var root = document.documentElement;

  // Senza GSAP tolgo la classe di guardia: il CSS torna a mostrare tutto normalmente.
  if (!window.gsap) {
    root.classList.remove('gsap-motion');
    return;
  }

  gsap.defaults({ duration: 0.7, ease: 'power2.out' });

  var REVEAL_SELECTOR = '[data-motion], .scroll-trigger';
  var observer = null;
  var parallaxItems = [];
  var rafPending = false;
  var mm = gsap.matchMedia();

  /* ---------- Utility ---------- */

  // Divide il testo in parole avvolte in span: serve per l'animazione dei titoli.
  // Lo faccio solo su elementi di solo testo, per non distruggere eventuale markup interno.
  function splitWords(el) {
    if (el.childElementCount > 0) return null;
    var testo = el.textContent.trim();
    if (!testo) return null;

    el.textContent = '';
    testo.split(/\s+/).forEach(function (parola, i, arr) {
      var riga = document.createElement('span');
      riga.className = 'motion-word';
      var inner = document.createElement('span');
      inner.className = 'motion-word__inner';
      inner.textContent = i === arr.length - 1 ? parola : parola + ' ';
      riga.appendChild(inner);
      el.appendChild(riga);
    });

    el.classList.add('motion-words');
    return el.querySelectorAll('.motion-word__inner');
  }

  function variante(el) {
    if (el.dataset.motion) return el.dataset.motion;
    // Ponte con Dawn: .animate--fade-in resta una dissolvenza, il resto sale dolcemente.
    return el.classList.contains('animate--fade-in') ? 'fade' : 'rise';
  }

  function bersagli(el) {
    if (!el.hasAttribute('data-motion-stagger')) return [el];
    var figli = el.querySelectorAll('[data-motion-item]');
    return figli.length ? Array.prototype.slice.call(figli) : Array.prototype.slice.call(el.children);
  }

  /* ---------- Stato iniziale + ingresso ---------- */

  function prepara(el) {
    if (el.dataset.motionState) return;
    el.dataset.motionState = 'pending';

    var v = variante(el);

    if (v === 'words') {
      var parole = splitWords(el);
      if (parole && parole.length) {
        el._motionWords = parole;
        gsap.set(el, { autoAlpha: 1 });
        gsap.set(parole, { yPercent: 110, autoAlpha: 0 });
        return;
      }
      // Nessuna parola da dividere: ricado sulla variante di default.
      el.dataset.motion = 'rise';
    }

    // Variante "unclip": sipario che si apre dal basso. Il ritaglio va sul media
    // interno (img/svg/video), MAI sull'elemento osservato: Chromium esclude dal
    // calcolo di IntersectionObserver l'area tolta da clip-path, e con inset(100%)
    // l'observer non scatterebbe mai (l'elemento resterebbe nascosto per sempre).
    if (v === 'unclip') {
      el._motionClip = el.querySelector('img, svg, video, iframe') || el;
      // Oltre al sipario, un leggero zoom d'assestamento (1.12 → 1) che si somma
      // all'eventuale scale CSS della parallasse: l'apertura "respira".
      gsap.set(el._motionClip, { clipPath: 'inset(100% 0% 0% 0%)', scale: 1.12 });
      gsap.set(el, { autoAlpha: 1 });
      return;
    }

    var stato = { autoAlpha: 0 };
    if (variante(el) === 'rise') stato.y = 28;
    if (variante(el) === 'zoom') stato.scale = 0.96;

    gsap.set(bersagli(el), stato);
    if (el.hasAttribute('data-motion-stagger')) gsap.set(el, { autoAlpha: 1 });
  }

  // A fine animazione passo il controllo al CSS (.motion-in) e ripulisco gli stili inline.
  function completa(el) {
    el.classList.add('motion-in');
    gsap.set(el, { clearProps: 'transform,opacity,visibility,clipPath' });
  }

  function rivela(el) {
    if (el.dataset.motionState === 'done') return;
    el.dataset.motionState = 'done';

    var delay = parseFloat(el.dataset.motionDelay) || 0;

    if (el._motionWords) {
      el.classList.add('motion-in');
      gsap.to(el._motionWords, {
        yPercent: 0,
        autoAlpha: 1,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.07,
        delay: delay
      });
      return;
    }

    if (variante(el) === 'unclip') {
      var clipTarget = el._motionClip || el;
      gsap.to(clipTarget, {
        clipPath: 'inset(0% 0% 0% 0%)',
        scale: 1,
        duration: 0.85,
        ease: 'power3.out',
        delay: delay,
        onComplete: function () {
          el.classList.add('motion-in');
          gsap.set(clipTarget, { clearProps: 'clipPath' });
        }
      });
      return;
    }

    var fine = {
      autoAlpha: 1,
      delay: delay,
      onComplete: function () {
        completa(el);
      }
    };

    var v = variante(el);
    if (v === 'rise') fine.y = 0;
    if (v === 'zoom') fine.scale = 1;

    var targets = bersagli(el);
    if (targets.length > 1) {
      fine.stagger = 0.09;
      fine.duration = 0.8;
    }

    gsap.to(targets, fine);
  }

  /* ---------- Parallasse (solo scroll, senza plugin) ---------- */

  function registraParallasse(el) {
    var img = el.matches('img') ? el : el.querySelector('img');
    if (!img || el.dataset.motionParallaxInit) return;
    el.dataset.motionParallaxInit = 'true';

    var forza = parseFloat(el.dataset.motionParallax) || 0.12;
    img.classList.add('motion-parallax__media');

    parallaxItems.push({
      contenitore: el,
      set: gsap.quickTo(img, 'yPercent', { duration: 0.35, ease: 'power2.out' }),
      forza: forza
    });
  }

  function aggiornaParallasse() {
    rafPending = false;
    var h = window.innerHeight;

    parallaxItems.forEach(function (item) {
      var r = item.contenitore.getBoundingClientRect();
      if (r.bottom < 0 || r.top > h) return;
      // -1 quando l'elemento sta uscendo in alto, 1 quando entra dal basso.
      var progresso = (r.top + r.height / 2 - h / 2) / (h / 2 + r.height / 2);
      item.set(gsap.utils.clamp(-1, 1, progresso) * item.forza * 100 * 0.15);
    });
  }

  function onScroll() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(aggiornaParallasse);
  }

  /* ---------- Hover delle card prodotto ---------- */

  function initHoverCard(scope) {
    scope.querySelectorAll('.card-wrapper').forEach(function (card) {
      if (card.dataset.motionHover) return;
      var img = card.querySelector('.card__media img');
      if (!img) return;
      card.dataset.motionHover = 'true';

      // Nota: quickTo non gestisce la shorthand "scale", quindi uso gsap.to con overwrite.
      var scala = function (valore) {
        gsap.to(img, { scale: valore, duration: 0.6, ease: 'power3.out', overwrite: 'auto' });
      };

      card.addEventListener('mouseenter', function () {
        scala(1.04);
      });
      card.addEventListener('mouseleave', function () {
        scala(1);
      });
    });
  }

  /* ---------- Rilevamento automatico dei bersagli ---------- */

  function autoTag(scope) {
    // Titolo dell'hero: animazione parola per parola (solo se è testo semplice).
    scope.querySelectorAll('.banner__heading').forEach(function (h) {
      if (!h.dataset.motion && h.childElementCount === 0) h.dataset.motion = 'words';
    });

    // Parallasse sulle immagini dei banner, a meno che il banner non usi già
    // un comportamento immagine di Dawn (fixed / zoom-in / ambient).
    scope.querySelectorAll('.banner__media').forEach(function (media) {
      if (media.className.indexOf('animate--fixed') > -1) return;
      if (media.className.indexOf('animate--zoom-in') > -1) return;
      if (media.className.indexOf('animate--ambient') > -1) return;
      if (!media.hasAttribute('data-motion-parallax')) media.setAttribute('data-motion-parallax', '0.12');
    });
  }

  /* ---------- Avvio ---------- */

  function init(scope) {
    scope = scope || document;
    autoTag(scope);

    scope.querySelectorAll(REVEAL_SELECTOR).forEach(function (el) {
      prepara(el);
      observer.observe(el);
    });

    scope.querySelectorAll('[data-motion-parallax]').forEach(registraParallasse);
    initHoverCard(scope);
  }

  function mostraTutto(scope) {
    scope = scope || document;
    scope.querySelectorAll(REVEAL_SELECTOR).forEach(function (el) {
      el.classList.add('motion-in');
      gsap.set(el, { clearProps: 'all' });
      if (el._motionClip) gsap.set(el._motionClip, { clearProps: 'clipPath' });
    });
  }

  mm.add(
    {
      anima: '(prefers-reduced-motion: no-preference)',
      riduci: '(prefers-reduced-motion: reduce)'
    },
    function (context) {
      // Motion ridotto: nessuna animazione, contenuto subito visibile.
      if (context.conditions.riduci) {
        root.classList.remove('gsap-motion');
        mostraTutto(document);
        return;
      }

      root.classList.add('gsap-motion');

      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            rivela(entry.target);
          });
        },
        { rootMargin: '0px 0px -4% 0px', threshold: 0.08 }
      );

      init(document);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();

      // Nel customizer le sezioni vengono ricaricate: rianimo solo il nodo aggiornato.
      function onSectionLoad(e) {
        init(e.target);
      }
      document.addEventListener('shopify:section:load', onSectionLoad);

      return function () {
        observer.disconnect();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        document.removeEventListener('shopify:section:load', onSectionLoad);
        parallaxItems = [];
      };
    }
  );

  window.ThemeMotion = { init: init, gsap: gsap };
})();
